import type { Tier, TierPerk, VoucherEffect } from '../schemas/loyalty.js';
import type { Money } from '../schemas/primitives.js';

/**
 * Pure pricing helper used by both POS preview and the recordPayment callable.
 *
 * Inputs: subtotal (sen — what the ticket snapshot says before any loyalty),
 *         the customer's current tier (or null),
 *         the customer-selected vouchers (snapshotted effects, order matters).
 *
 * Order of operations:
 *   1. Tier discount applies first to the subtotal.
 *   2. Vouchers apply in the order the cashier picked them, each against the
 *      running total after tier + earlier vouchers (so cumulative discounts
 *      compound — the cashier sees this in the breakdown and decides if it's
 *      what they want).
 *   3. Total is clamped at 0 (you can't owe a negative).
 *
 * Returns a structured breakdown so the UI can show every line and the
 * callable can audit-log it identically.
 *
 * Sen integer arithmetic throughout — no floats, no rounding surprises.
 */

export interface DiscountLine {
  readonly kind: 'promotion' | 'tier' | 'voucher';
  /** Stable id — `promotion:{id}` / `tier:{tierId}` / `voucher:{voucherId}` — handy for keys. */
  readonly id: string;
  /** Human label (use locale-resolved name when calling). */
  readonly label: string;
  /** Discount amount in sen (positive number to subtract). */
  readonly amount: Money;
}

export interface PriceBreakdown {
  readonly subtotal: Money;
  readonly discountLines: readonly DiscountLine[];
  readonly discountTotal: Money;
  readonly total: Money;
}

export interface VoucherForPricing {
  readonly id: string;
  readonly label: string;
  readonly effect: VoucherEffect;
}

/**
 * Promotions resolved up-front at bookTicket time (see resolvePromotions).
 * Passed into pricing so the cashier sees them on the breakdown the same way
 * vouchers and tier discounts appear — but as a separate, pre-applied band
 * sitting between subtotal and the tier/voucher pile. The actual savings
 * value is taken from the ticket snapshot (server-of-record), so the cashier
 * cannot accidentally re-charge the customer for a discount the booking flow
 * already gave them.
 */
export interface PromotionForPricing {
  readonly id: string;
  readonly label: string;
  readonly savings: Money;
}

export interface PriceWithDiscountsInput {
  readonly subtotal: Money;
  readonly promotions?: readonly PromotionForPricing[];
  readonly tier?: Tier | null;
  readonly tierLabel?: string;
  readonly vouchers?: readonly VoucherForPricing[];
}

export function priceWithDiscounts(input: PriceWithDiscountsInput): PriceBreakdown {
  const subtotal = Math.max(0, Math.trunc(input.subtotal));
  const lines: DiscountLine[] = [];

  let running = subtotal;

  // Promotions first — they were resolved at booking time so their savings
  // are authoritative. The resolver already capped them at subtotal, so the
  // cashier can trust the number without re-deriving it.
  for (const p of input.promotions ?? []) {
    if (running <= 0) break;
    const amount = Math.min(running, Math.max(0, Math.trunc(p.savings)));
    if (amount <= 0) continue;
    lines.push({
      kind: 'promotion',
      id: `promotion:${p.id}`,
      label: p.label,
      amount,
    });
    running -= amount;
  }

  const tierDiscount = computeTierDiscount(running, input.tier ?? null);
  if (tierDiscount > 0 && input.tier) {
    lines.push({
      kind: 'tier',
      id: `tier:${input.tier.id}`,
      label: input.tierLabel ?? `Tier ${input.tier.id}`,
      amount: tierDiscount,
    });
    running -= tierDiscount;
  }

  for (const v of input.vouchers ?? []) {
    if (running <= 0) break;
    const disc = computeVoucherDiscount(running, v.effect);
    if (disc <= 0) continue;
    lines.push({
      kind: 'voucher',
      id: `voucher:${v.id}`,
      label: v.label,
      amount: disc,
    });
    running -= disc;
  }

  const discountTotal = lines.reduce((s, l) => s + l.amount, 0);
  const total = Math.max(0, subtotal - discountTotal);
  return { subtotal, discountLines: lines, discountTotal, total };
}

/** First `discount_pct` perk on the tier wins; returns 0 if none. */
function computeTierDiscount(amount: Money, tier: Tier | null): Money {
  if (!tier) return 0;
  const perk = tier.perks.find(
    (p): p is Extract<TierPerk, { kind: 'discount_pct' }> => p.kind === 'discount_pct',
  );
  if (!perk) return 0;
  return Math.round((amount * perk.value) / 100);
}

function computeVoucherDiscount(amount: Money, effect: VoucherEffect): Money {
  switch (effect.type) {
    case 'pct_off':
      return Math.min(amount, Math.round((amount * effect.value) / 100));
    case 'fixed_off':
      return Math.min(amount, Math.max(0, Math.trunc(effect.value)));
    case 'free_service':
    case 'free_package':
      // MVP: free_service/free_package require service-level breakdown to apply
      // correctly. The POS doesn't yet split the cart by service, so we treat
      // these as a no-op here and surface a clear "applies to specific item"
      // message in the UI. Sprint 11 wires the line-item path.
      return 0;
  }
}
